import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CrosswordGrid from './CrosswordGrid';
import CluesList from './CluesList';
import { Puzzle, PuzzleAttempt, User } from '../types';
import { attemptAPI, authAPI } from '../api';
import { getGuestId, getDisplayName, setDisplayName } from '../utils/identity';
import { wasRevealed, markRevealed, clearRevealed } from '../utils/reveals';
import { formatDuration, formatDurationMs } from '../utils/format';
import {
  colors,
  fonts,
  radius,
  shadow,
  numeric,
  sectionLabel,
  buttonPrimary,
  buttonSecondary,
  focusRing,
} from '../theme';

const PlayerContainer = styled.div`
  background: ${colors.paper};
  padding: 28px 20px 48px;
`;

const PuzzleHeader = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;

const Title = styled.h1`
  font-family: ${fonts.display};
  font-size: 28px;
  margin: 0 0 6px;
  color: ${colors.ink};
`;

const Timer = styled.div<{ $paused?: boolean }>`
  ${numeric}
  font-size: 19px;
  font-weight: 600;
  color: ${props => (props.$paused ? colors.inkFaint : colors.inkSoft)};
`;

const GameSection = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
  justify-content: center;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
    gap: 28px;
  }
`;

const GridSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  ${buttonPrimary}
  ${focusRing}
`;

const SecondaryButton = styled.button`
  ${buttonSecondary}
  ${focusRing}
`;

/* ---- Start screen ---- */

const StartPanel = styled.div`
  max-width: 400px;
  margin: 72px auto;
  text-align: center;
`;

const StartHeading = styled.p`
  font-family: ${fonts.display};
  font-size: 19px;
  color: ${colors.ink};
  margin: 0 0 20px;
`;

const StartButton = styled.button`
  ${buttonPrimary}
  ${focusRing}
  font-size: 16px;
  padding: 13px 40px;
`;

const StartHint = styled.p`
  margin: 14px 0 0;
  font-size: 13px;
  color: ${colors.inkFaint};

  kbd {
    ${numeric}
    font-size: 12px;
    border: 1px solid ${colors.ruleStrong};
    border-bottom-width: 2px;
    border-radius: ${radius.sm};
    padding: 1px 6px;
    background: ${colors.card};
  }
`;

/* ---- Completion ---- */

const DoneCard = styled.div`
  max-width: 400px;
  margin: 0 auto;
  background: ${colors.card};
  border: 1px solid ${colors.rule};
  border-top: 3px solid ${colors.success};
  border-radius: ${radius.md};
  box-shadow: ${shadow.panel};
  padding: 22px 24px;
  text-align: center;
`;

const DoneLabel = styled.div`
  ${sectionLabel}
  color: ${colors.success};
`;

const DoneTime = styled.div`
  ${numeric}
  font-size: 40px;
  font-weight: 700;
  color: ${colors.ink};
  line-height: 1.1;
  margin: 6px 0 2px;
`;

const DoneCaption = styled.div`
  font-size: 13px;
  color: ${colors.inkFaint};
  margin-bottom: 18px;
`;

const DoneActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
`;

const NameForm = styled.form`
  display: flex;
  gap: 8px;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid ${colors.rule};
`;

const NameInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid ${colors.ruleStrong};
  border-radius: ${radius.sm};
  background: ${colors.card};
  color: ${colors.ink};
  font-size: 14px;

  &::placeholder {
    color: ${colors.inkFaint};
  }

  &:focus {
    outline: none;
    border-color: ${colors.accent};
  }
`;

const NameSaved = styled.p`
  margin: 18px 0 0;
  padding-top: 18px;
  border-top: 1px solid ${colors.rule};
  font-size: 13px;
  color: ${colors.inkSoft};
`;

const Notice = styled.div`
  max-width: 400px;
  margin: 0 auto 16px;
  padding: 11px 16px;
  background: ${colors.dangerWash};
  border: 1px solid ${colors.danger};
  border-radius: ${radius.sm};
  color: ${colors.danger};
  font-size: 14px;
  text-align: center;
`;

const RevealedNotice = styled.div`
  max-width: 400px;
  margin: 0 auto;
  text-align: center;
  font-size: 13px;
  color: ${colors.inkFaint};
`;

const Toast = styled.div<{ $visible: boolean }>`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%)
    translateY(${props => (props.$visible ? '0' : '10px')});
  background: ${colors.ink};
  color: ${colors.paper};
  padding: 11px 20px;
  border-radius: ${radius.sm};
  font-size: 14px;
  box-shadow: ${shadow.raised};
  opacity: ${props => (props.$visible ? 1 : 0)};
  transition: opacity 0.16s ease, transform 0.16s ease;
  pointer-events: none;
  z-index: 1001;
`;

interface CrosswordPlayerProps {
  puzzle: Puzzle;
  currentUser?: User | null;
}

export const CrosswordPlayer: React.FC<CrosswordPlayerProps> = ({ puzzle, currentUser }) => {
  const navigate = useNavigate();
  const gridRef = React.useRef<{
    navigateToClue: (clueNumber: string, clueDirection: 'across' | 'down') => void;
  } | null>(null);

  const [currentGrid, setCurrentGrid] = useState<string[][]>(() =>
    Array(puzzle.height).fill(null).map(() => Array(puzzle.width).fill(''))
  );
  const [attempt, setAttempt] = useState<PuzzleAttempt | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(Date.now());
  const [isCompleted, setIsCompleted] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [checkResult, setCheckResult] = useState<'correct' | 'incorrect' | null>(null);
  const [guestId] = useState<string>(getGuestId);
  const [activeClueNumber, setActiveClueNumber] = useState<string | undefined>();
  const [activeDirection, setActiveDirection] = useState<'across' | 'down'>('across');
  const [secondaryClueNumber, setSecondaryClueNumber] = useState<string | undefined>();
  const [secondaryDirection, setSecondaryDirection] = useState<'across' | 'down' | undefined>();
  const [toast, setToast] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [savedName, setSavedName] = useState<string | null>(() => getDisplayName());
  const [isRevealed, setIsRevealed] = useState<boolean>(() => wasRevealed(puzzle.id));

  // Initialize active clue to the first across clue
  useEffect(() => {
    if (puzzle && !activeClueNumber) {
      const firstAcrossClue = Object.keys(puzzle.across_clues)
        .map(Number)
        .sort((a, b) => a - b)[0];
      if (firstAcrossClue) {
        setActiveClueNumber(firstAcrossClue.toString());
        setActiveDirection('across');
      }
    }
  }, [puzzle, activeClueNumber]);

  const handleStart = useCallback(() => {
    setIsStarted(true);
    setStartTime(Date.now());
  }, []);

  // Enter starts the game from the ready screen
  useEffect(() => {
    if (isStarted) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleStart();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isStarted, handleStart]);

  // Timer
  useEffect(() => {
    if (isCompleted || !isStarted || startTime === null) return;

    const interval = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isCompleted, isStarted, startTime]);

  // Auto-hide the toast
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(timer);
  }, [toast]);

  // Load or create this solver's attempt
  useEffect(() => {
    const loadAttempt = async () => {
      try {
        const existingAttempt = await attemptAPI.getUserAttempt(
          puzzle.id,
          currentUser ? undefined : guestId
        );
        if (existingAttempt) {
          setAttempt(existingAttempt);
          setCurrentGrid(existingAttempt.current_grid);
          setIsCompleted(existingAttempt.is_completed || isRevealed);
          if (existingAttempt.is_completed) {
            setCheckResult('correct');
            setIsStarted(true);
          } else if (isRevealed) {
            // Show the revealed board as finished, but not as a solve.
            setIsStarted(true);
          }
        } else {
          const newAttempt = await attemptAPI.createAttempt(
            puzzle.id,
            currentUser ? undefined : guestId,
            puzzle.width,
            puzzle.height
          );
          setAttempt(newAttempt);
        }
      } catch (error) {
        console.error('Error loading attempt:', error);
      }
    };

    loadAttempt();
  }, [puzzle.id, puzzle.width, puzzle.height, currentUser, guestId, isRevealed]);

  const handleCellChange = async (row: number, col: number, value: string) => {
    // Copy the affected row too — mutating it in place would edit state directly.
    const newGrid = currentGrid.map((gridRow, index) =>
      index === row ? [...gridRow] : gridRow
    );
    newGrid[row][col] = value;
    setCurrentGrid(newGrid);

    if (attempt) {
      try {
        const updatedAttempt = await attemptAPI.updateAttempt(attempt.id, {
          current_grid: newGrid,
        });
        setAttempt(updatedAttempt);
      } catch (error) {
        console.error('Error saving attempt:', error);
      }
    }
  };

  const isGridComplete = () => {
    for (let row = 0; row < puzzle.height; row++) {
      for (let col = 0; col < puzzle.width; col++) {
        if (puzzle.solution[row][col] !== '#' && !currentGrid[row][col]) {
          return false;
        }
      }
    }
    return true;
  };

  const checkSolution = () => {
    for (let row = 0; row < puzzle.height; row++) {
      for (let col = 0; col < puzzle.width; col++) {
        if (
          puzzle.solution[row][col] !== '#' &&
          currentGrid[row][col] !== puzzle.solution[row][col]
        ) {
          return false;
        }
      }
    }
    return true;
  };

  const markCompleted = async (grid: string[][]) => {
    const completionTime = Math.floor((Date.now() - (startTime || Date.now())) / 1000);
    setIsCompleted(true);
    setCheckResult('correct');

    if (!attempt) return;
    try {
      const updated = await attemptAPI.updateAttempt(attempt.id, {
        current_grid: grid,
        is_completed: true,
        completion_time: completionTime,
        // Carry the guest's saved name onto the attempt so they show up by
        // name on the leaderboard without being asked again.
        ...(!currentUser && savedName ? { display_name: savedName } : {}),
      });
      setAttempt(updated);
    } catch (error) {
      console.error('Error updating completion:', error);
    }
  };

  // Auto-check once every square is filled.
  // Note: checkSolution and isGridComplete are internal helpers derived from
  // puzzle state, so they are intentionally left out of the dependency list.
  useEffect(() => {
    // A revealed grid is already correct — never score it as a solve.
    if (!isStarted || isCompleted || isRevealed) return;
    if (!isGridComplete()) return;

    if (checkSolution()) {
      markCompleted(currentGrid);
    } else {
      setCheckResult('incorrect');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGrid, isStarted, isCompleted, isRevealed, attempt, startTime]);

  const handleCheckAnswer = () => {
    if (isRevealed) return;
    if (checkSolution()) {
      markCompleted(currentGrid);
    } else {
      setCheckResult('incorrect');
    }
  };

  const handleRevealSolution = async () => {
    if (!window.confirm('Reveal the solution? This ends your current game.')) return;

    markRevealed(puzzle.id);
    setIsRevealed(true);
    setCurrentGrid(puzzle.solution);
    setIsCompleted(true);
    setCheckResult(null);

    if (attempt) {
      try {
        // Revealed puzzles are saved as progress but never as a finished
        // solve, so they can't land on the leaderboard.
        const updated = await attemptAPI.updateAttempt(attempt.id, {
          current_grid: puzzle.solution,
        });
        setAttempt(updated);
      } catch (error) {
        console.error('Error updating attempt:', error);
      }
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset your progress on this puzzle?')) return;

    const newGrid = Array(puzzle.height).fill(null).map(() => Array(puzzle.width).fill(''));
    clearRevealed(puzzle.id);
    setIsRevealed(false);
    setCurrentGrid(newGrid);
    setIsCompleted(false);
    setCheckResult(null);
    setStartTime(Date.now());

    if (attempt) {
      try {
        const updated = await attemptAPI.updateAttempt(attempt.id, {
          current_grid: newGrid,
          is_completed: false,
          completion_time: undefined,
        });
        setAttempt(updated);
      } catch (error) {
        console.error('Error resetting attempt:', error);
      }
    }
  };

  const elapsedMs = isCompleted && attempt?.completion_time
    ? attempt.completion_time * 1000
    : currentTime - (startTime || currentTime);

  const copyToClipboard = async (text: string) => {
    try {
      // clipboard.writeText needs a secure context; fall back when it's absent.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      return true;
    } catch (error) {
      console.error('Copy error:', error);
      return false;
    }
  };

  const handleShareTime = async () => {
    const time = attempt?.completion_time
      ? formatDuration(attempt.completion_time)
      : formatDurationMs(elapsedMs);

    // Link straight to this puzzle so whoever reads it can play the same one.
    const puzzleUrl = `${window.location.origin}/puzzle/${puzzle.id}`;
    const text = `${puzzle.title} — solved in ${time}\n${puzzleUrl}`;

    const copied = await copyToClipboard(text);
    setToast(copied ? 'Time and link copied' : 'Could not copy — try again');
  };

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = nameDraft.trim();
    if (!name) return;

    setDisplayName(name);
    setSavedName(name);

    try {
      if (currentUser) {
        await authAPI.updateDisplayName(name);
      } else if (attempt) {
        await attemptAPI.updateAttempt(attempt.id, {
          current_grid: currentGrid,
          display_name: name,
        });
      }
      setToast('Added to the leaderboard');
    } catch (error) {
      console.error('Error saving name:', error);
      setToast('Could not save your name');
    }
  };

  const handleClueChange = (
    clueNumber: string,
    direction: 'across' | 'down',
    secondaryClue?: string,
    secondaryDir?: 'across' | 'down'
  ) => {
    setActiveClueNumber(clueNumber);
    setActiveDirection(direction);
    setSecondaryClueNumber(secondaryClue);
    setSecondaryDirection(secondaryDir);
  };

  const handleClueClick = (clueNumber: string, direction: 'across' | 'down') => {
    setActiveClueNumber(clueNumber);
    setActiveDirection(direction);
    // The grid sets the secondary clue when the cell takes focus.
    setSecondaryClueNumber(undefined);
    setSecondaryDirection(undefined);
    gridRef.current?.navigateToClue(clueNumber, direction);
  };

  const displayedName = currentUser?.display_name || savedName;

  return (
    <PlayerContainer>
      <PuzzleHeader>
        <Title>{puzzle.title}</Title>
        {isStarted && startTime && (
          <Timer $paused={isCompleted}>{formatDurationMs(elapsedMs)}</Timer>
        )}
      </PuzzleHeader>

      {!isStarted ? (
        <StartPanel>
          <StartHeading>Ready to solve?</StartHeading>
          <StartButton onClick={handleStart}>Start the clock</StartButton>
          <StartHint>
            or press <kbd>Enter</kbd>
          </StartHint>
        </StartPanel>
      ) : (
        <>
          <GameSection>
            <GridSection>
              <CrosswordGrid
                ref={gridRef}
                grid={currentGrid}
                width={puzzle.width}
                height={puzzle.height}
                solution={puzzle.solution}
                onCellChange={handleCellChange}
                readonly={isCompleted}
                showNumbers={true}
                acrossClues={puzzle.across_clues}
                downClues={puzzle.down_clues}
                onClueChange={handleClueChange}
                onStartGame={handleStart}
              />

              {checkResult === 'incorrect' && !isCompleted && (
                <Notice>Something's not right yet — keep at it.</Notice>
              )}

              {isRevealed && (
                <RevealedNotice>
                  Answers revealed — this one won't count on the leaderboard.
                </RevealedNotice>
              )}

              {isCompleted && checkResult === 'correct' ? (
                <DoneCard>
                  <DoneLabel>Solved</DoneLabel>
                  <DoneTime>
                    {attempt?.completion_time
                      ? formatDuration(attempt.completion_time)
                      : formatDurationMs(elapsedMs)}
                  </DoneTime>
                  <DoneCaption>Nicely done.</DoneCaption>

                  <DoneActions>
                    <PrimaryButton onClick={handleShareTime}>
                      Copy my time to share
                    </PrimaryButton>
                    <SecondaryButton onClick={() => navigate(`/leaderboard/${puzzle.id}`)}>
                      Leaderboard
                    </SecondaryButton>
                  </DoneActions>

                  {displayedName ? (
                    <NameSaved>
                      You're on the board as <strong>{displayedName}</strong>.
                    </NameSaved>
                  ) : (
                    <NameForm onSubmit={handleSaveName}>
                      <NameInput
                        value={nameDraft}
                        onChange={e => setNameDraft(e.target.value)}
                        placeholder="Your name for the leaderboard"
                        maxLength={24}
                        aria-label="Your name for the leaderboard"
                      />
                      <PrimaryButton type="submit">Add</PrimaryButton>
                    </NameForm>
                  )}
                </DoneCard>
              ) : (
                <ButtonRow>
                  <PrimaryButton onClick={handleCheckAnswer} disabled={isCompleted}>
                    Check answer
                  </PrimaryButton>
                  <SecondaryButton onClick={handleRevealSolution} disabled={isCompleted}>
                    Reveal
                  </SecondaryButton>
                  <SecondaryButton onClick={handleReset}>Reset</SecondaryButton>
                </ButtonRow>
              )}
            </GridSection>

            <CluesList
              acrossClues={puzzle.across_clues}
              downClues={puzzle.down_clues}
              activeClueNumber={activeClueNumber}
              activeDirection={activeDirection}
              secondaryClueNumber={secondaryClueNumber}
              secondaryDirection={secondaryDirection}
              onClueClick={handleClueClick}
            />
          </GameSection>

          <Toast $visible={toast !== null} role="status">
            {toast}
          </Toast>
        </>
      )}
    </PlayerContainer>
  );
};

export default CrosswordPlayer;
